import API from "helpers/api";
import { Apis } from "services/api";
import { getAPIResponse } from "helpers/getApiResponse";
import { defaultPageSize } from "helpers/utils";
import { BASE_API_PATH } from "helpers/constants";
import { processChipsForAPI } from "helpers/chipProcessor";

// Utility function to transform old API parameters to new ServiceSearchPostRequest format
const transformToServiceSearchRequest = ({
	search = "",
	siteDepartmentIds = [], // Multiple departments (from multi-select filter)
	statusType = "",
	status = "",
	chips = [],
	...otherParams
}) => {
	// Process chips using centralized utility function
	const chipsByType = processChipsForAPI(chips);

	// Handle all department parameters and ensure siteDepartmentIds is always an array
	const allDepartmentIds = [];

	// Multi-select departments (already array)
	if (siteDepartmentIds && Array.isArray(siteDepartmentIds)) {
		allDepartmentIds.push(...siteDepartmentIds);
	}
	// Add all department IDs to chipsByType.siteDepartmentIds
	allDepartmentIds.forEach(deptId => {
		const id = parseInt(deptId);
		if (!isNaN(id) && id !== 0 && !chipsByType.siteDepartmentIds.includes(id)) {
			chipsByType.siteDepartmentIds.push(id);
		}
	});

	// Add status from filter to statuses array if present
	if (status && status !== "") {
		if (!chipsByType.statuses.includes(status)) {
			chipsByType.statuses.push(status);
		}
	}

	// Add search text to textSearches array if present
	if (search && search !== "") {
		if (!chipsByType.textSearches.includes(search)) {
			chipsByType.textSearches.push(search);
		}
	}

	// Get timezone
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	return {
		...otherParams,
		timeZone,
		statusType, // Preserve statusType as separate parameter
		...chipsByType
	};
};

export const postNewService = async (payload) => {
	try {
		let response = await API.post(`${Apis.Services}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getServicesList = async ({
	siteAppId,
	pageNumber = "1",
	pageSize = defaultPageSize(),
	search = "",
	sortField = "",
	sort = "",
	fromDate = "",
	toDate = "",
	siteDepartmentID = "",
	statusType = "",
	status = "",
}) => {
	try {
		let response = await API.get(
			`${
				Apis.Services
			}?siteAppId=${siteAppId}&fromDate=${fromDate}&toDate=${toDate}&siteDepartmentID=${
				siteDepartmentID ?? ""
			}&statusType=${statusType}&status=${status}&pageNumber=${pageNumber}&pageSize=${pageSize}&search=${search}&sortField=${sortField}&sort=${sort}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
export const getModelTaskList = async ({
	siteAppId,
	pageNumber = "1",
	pageSize = defaultPageSize(),
	search = "",
	sortField = "",
	sort = "",
}) => {
	try {
		let response = await API.get(
			`${Apis.Models}?modelId=${siteAppId}&pageNumber=${pageNumber}&pageSize=${pageSize}&search=${search}&sortField=${sortField}&sort=${sort}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
export const getCountOfModelTaskList = async ({
	siteAppId,
	search = "",
	fromDate = "",
	toDate = "",
	siteDepartmentID = "",
	statusType = "",
	status = "",
}) => {
	try {
		let response = await API.get(
			`${
				Apis.Models
			}/count?siteAppId=${siteAppId}&fromDate=${fromDate}&toDate=${toDate}&siteDepartmentID=${
				siteDepartmentID ?? ""
			}&statusType=${statusType}&status=${status}&search=${search}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
export const getCountOfServiceList = async ({
	siteAppId,
	search = "",
	fromDate = "",
	toDate = "",
	siteDepartmentID = "",
	siteDepartmentIds = [],
	statusType = "",
	status = "",
	chips = [],
}) => {
	try {
		// Handle both old single department ID and new multi-select department IDs
		let departmentIds = [];

		// If multi-select siteDepartmentIds is provided, use it
		if (siteDepartmentIds && siteDepartmentIds.length > 0) {
			departmentIds = siteDepartmentIds;
		} else if (siteDepartmentID && siteDepartmentID !== "") {
			// Fall back to single department ID for backward compatibility
			departmentIds = [siteDepartmentID];
		}

		const payload = transformToServiceSearchRequest({
			fromDate,
			toDate,
			siteAppId,
			search,
			siteDepartmentIds: departmentIds,
			statusType,
			status,
			chips
		});

		let response = await API.post(`${Apis.Services}/count`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const importService = async (payload) => {
	try {
		let response = await API.post(`${Apis.Services}/import`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getSiteDepartmentsInService = async (id) => {
	try {
		let response = await API.get(`${Apis.SiteDepartments}?siteId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const deleteNoticeboardDepartment = async (id) => {
	try {
		let response = await API.delete(`${Apis.NoticeboardDepartments}/${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getNoticeboardDepartment = async (id) => {
	try {
		let response = await API.get(
			`${Apis.NoticeboardDepartments}?noticeboardId=${id}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const updateNoticeboardDepartment = async (payload) => {
	try {
		let response = await API.post(`${Apis.NoticeboardDepartments}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const DownloadCSVTemplate = async () => {
	try {
		let response = await API.get(`${Apis.Services}/downloadImportTemplate `);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
export const DownloadCSVTemplateforModalTask = async () => {
	try {
		let response = await API.get(
			`${BASE_API_PATH}ModelVersionTasks/downloadTaskImportTemplate  `
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const DownloadCSVTemplateForSiteSetting = async () => {
	try {
		let response = await API.get(`${Apis.SiteAssets}/downloadImportTemplate  `);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getServiceAutocompleteSearch = async ({ search = "" }) => {
	try {
		let response = await API.get(`${Apis.Services}/autocomplete/${search}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getServicesSearch = async ({
	pageNumber = "1",
	pageSize = defaultPageSize(),
	search = "",
	sortField = "",
	sort = "",
	fromDate = "",
	toDate = "",
	siteDepartmentIds = [],
	statusType = "",
	status = "",
	chips = [],
	siteAppId,
}) => {
	try {
		const payload = transformToServiceSearchRequest({
			pageNumber,
			pageSize,
			search,
			sortField,
			sort,
			fromDate,
			toDate,
			siteDepartmentIds,
			statusType,
			status,
			chips,
			siteAppId
		});
		
		let response = await API.post(`${Apis.Services}/Search`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export const getServicesGroupedSearch = async ({
	pageNumber = 1,
	pageSize = 10,
	textSearches = [],
	sortField = "scheduledDate",
	sort = "asc",
	fromDate = "",
	toDate = "",
	siteDepartmentIds = [],
	siteAssetIds = [],
	modelIds = [],
	serviceWorkOrders = [],
	intervals = [],
	roles = [],
	statuses = [],
	statusType = "",
	siteAppId,
	includeDeleted = false,
	// Legacy parameters for backward compatibility
	search = "",
	status = "",
	chips = [],
}) => {
	try {
		// Use the same transformation logic as getServicesSearch
		const payload = transformToServiceSearchRequest({
			pageNumber,
			pageSize,
			search,
			sortField,
			sort,
			fromDate,
			toDate,
			siteDepartmentIds,
			statusType,
			status,
			chips,
			siteAppId,
			// Add new parameters that weren't in the old API
			textSearches,
			siteAssetIds,
			modelIds,
			serviceWorkOrders,
			intervals,
			roles,
			statuses,
			includeDeleted,
		});

		// Add includeDeleted to payload if not already there
		if (includeDeleted !== undefined && !payload.hasOwnProperty('includeDeleted')) {
			payload.includeDeleted = includeDeleted;
		}
		
		let response = await API.post(`${Apis.Services}/GroupedSearch`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
